import React, { useEffect, useState } from "react";
import moment from "moment";
import {
	Box,
	Button,
	Card,
	CardContent,
	Chip,
	ListItem,
	Skeleton,
	Stack,
	ToggleButton,
	ToggleButtonGroup,
	Tooltip,
	Typography,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InventoryIcon from "@mui/icons-material/Inventory";
import DownloadIcon from "@mui/icons-material/Download";
import AddIcon from "@mui/icons-material/Add";

import DefaultTable from "../../../components/Table/DefaultTable";
import { RootState } from "../../../store/store";
import { getUserDealerByID } from "../../../apis/dealer";
import { getQuoteByDealerID } from "../../../apis/quote";
import { useModelData } from "../../../hook/useModelData";
import { ModelOptionDataType } from "../../../types/global.types";
import { Model } from "../../../utils/model";
import { getOptionData1, subTotal } from "../../../utils/quote";
import { useOptionData } from "../../../hook/useOptionData";
import { useCompanyData } from "../../../hook/useCompanyData";

type DateRange = "30days" | "lastMonth" | "yearToDate" | "allTime";

const dateRangeOptions: { value: DateRange; label: string }[] = [
	{ value: "30days", label: "Last 30 Days" },
	{ value: "lastMonth", label: "Last Month" },
	{ value: "yearToDate", label: "Year to Date" },
	{ value: "allTime", label: "All Time" },
];

interface KPICardProps {
	title: string;
	value: string | number;
	icon: React.ReactNode;
	color: string;
	loading?: boolean;
}

const KPICard: React.FC<KPICardProps> = ({ title, value, icon, color, loading }) => (
	<Box sx={{ flex: "1 1 220px", minWidth: 200 }}>
		<Card
			sx={{
				height: "100%",
				borderLeft: `4px solid ${color}`,
				transition: "box-shadow 0.2s ease",
				"&:hover": {
					boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
				},
			}}
		>
			<CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
				<Stack direction="row" alignItems="center" justifyContent="space-between">
					<Box>
						{loading ? (
							<>
								<Skeleton width={80} height={40} />
								<Skeleton width={100} height={20} />
							</>
						) : (
							<>
								<Typography
									variant="h1"
									sx={{
										fontSize: "1.75rem",
										fontWeight: 700,
										color: "text.primary",
										lineHeight: 1.2,
									}}
								>
									{value}
								</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									{title}
								</Typography>
							</>
						)}
					</Box>
					<Box
						sx={{
							width: 48,
							height: 48,
							borderRadius: 2,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							bgcolor: `${color}14`,
							color: color,
							flexShrink: 0,
						}}
					>
						{icon}
					</Box>
				</Stack>
			</CardContent>
		</Card>
	</Box>
);

const statusChipColor = (status: string): { bg: string; color: string } => {
	const s = status?.toLowerCase();
	if (s === "sold") return { bg: "#ECFDF5", color: "#059669" };
	if (s === "expired") return { bg: "#FEF2F2", color: "#DC2626" };
	return { bg: "#EFF6FF", color: "#2563EB" };
};

const headerData: string[] = [
	`${process.env.REACT_APP_DEALER_FULL_NAME}`,
	"Contact",
	"Opt. Reference",
	"Quote #",
	"Date",
	"Status",
	`${process.env.REACT_APP_DEALER_NAME} Price`,
	"MSRP",
];

export const UserDashboard: React.FC = () => {
	const history = useNavigate();
	const currentUser = useSelector((state: RootState) => state.userAuth.user);

	const { models } = useModelData("dealer");
	const { company } = useCompanyData("dealer");
	const { options } = useOptionData("dealer");

	const [dealer, setDealer] = useState<any>(null);
	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [page, setPage] = useState<number>(0);
	const [pageSize, setPageSize] = useState<number>(20);
	const [count, setCount] = useState<number>(0);
	const [quotes, setQuotes] = useState([]);
	const [data, setData] = useState<any>();
	const [snapshotTerm, setSnapshotTerm] = useState<DateRange>("30days");

	// Stats derived from quotes
	const [totalSold, setTotalSold] = useState<number>(0);
	const [ytdCount, setYtdCount] = useState<number>(0);

	useEffect(() => {
		const fetchDealer = async () => {
			try {
				const dealerData = await getUserDealerByID({ ID: currentUser.id });
				setDealer({ ...dealerData });
				setIsLoading(false);
			} catch (error) {
				console.error("Failed to fetch dealer", error);
			}
		};

		if (currentUser?.id) {
			fetchDealer();
		} else {
			setIsLoading(false);
		}
	}, [currentUser]);

	const priceCalculation = (data: any, type: string) => {
		const modelOptionData = data?.model_option_ids
			? JSON.parse(data?.model_option_ids)
			: [];

		return modelOptionData?.map((res: ModelOptionDataType) => {
			let _model = models.find(
				(model: Model) => model.ID === res.model_id?.ID
			);

			return {
				model: {
					model_name: _model?.model_name,
					part_number: _model?.part_number,
					dealer_price: res.model_id?.[type],
					MSRP: res.model_id?.MSRP,
				},
				options: getOptionData1(options, res.option_ids),
				qty: res.qty,
			};
		});
	};

	const fetchQuotes = async () => {
		try {
			const { data: quoteData, counts, allData } = await getQuoteByDealerID({
				dealer_id: currentUser?.id,
				page: page + 1,
				pageSize,
			});

			const transformedData = quoteData?.map((quote: any, idx: number) => {
				const shouldTruncate = quote.opt_reference?.length > 50;
				const truncatedReference = shouldTruncate
					? `${quote.opt_reference.slice(0, 50)}...`
					: quote.opt_reference;

				const optReferenceElement = shouldTruncate ? (
					<Tooltip key={`optRef-${idx}`} title={quote.opt_reference}>
						<span>{truncatedReference}</span>
					</Tooltip>
				) : (
					<span>{truncatedReference}</span>
				);

				const { bg, color } = statusChipColor(quote.quote_status);

				return {
					dealer: quote?.dealer_name,
					contact: quote?.contact_name,
					opt_reference: optReferenceElement,
					quotes: (
						<ListItem
							disablePadding
							component={Link}
							to={`/_quote/${quote.quoteId}`}
						>
							{quote.no}
						</ListItem>
					),
					last_activity: moment(quote.createdAt).format("MM/DD/YYYY"),
					status: (
						<Chip
							label={quote.quote_status}
							size="small"
							sx={{
								bgcolor: bg,
								color: color,
								fontWeight: 600,
								fontSize: "0.75rem",
							}}
						/>
					),
					dealer_price: `$${subTotal(priceCalculation(quote, "dealer_price"), company, "dealer_price")?.toFixed(2)}`,
					msrp: `$${subTotal(priceCalculation(quote, "MSRP"), company, "MSRP")?.toFixed(2)}`,
				};
			});

			setData(allData);
			setQuotes(transformedData);
			setCount(counts);

			// Compute stats from allData
			const sold = allData?.filter((q: any) => q.quote_status?.toLowerCase() === "sold")?.length || 0;
			setTotalSold(sold);

			const startOfYear = moment().startOf("year");
			const ytd = allData?.filter((q: any) => moment(q.createdAt).isSameOrAfter(startOfYear))?.length || 0;
			setYtdCount(ytd);

			setIsLoading(false);
		} catch (error) {
			console.error("Failed to fetch admins", error);
			setIsLoading(false);
		}
	};

	useEffect(() => {
		setIsLoading(true);
		if (!currentUser) return;

		fetchQuotes();
	}, [currentUser, page, pageSize, company]);

	const handleDateRangeChange = (_: React.MouseEvent<HTMLElement>, newValue: DateRange | null) => {
		if (newValue !== null) {
			setPage(0);
			setSnapshotTerm(newValue);
		}
	};

	const handleChangePage = (page: number) => {
		setPage(page);
	};

	const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
		setPage(0);
		setPageSize(Number(e.target.value));
	};

	const priceValue = (index: number, type: string) => {
		const modelOptionData = data?.[index]?.model_option_ids
			? JSON.parse(data?.[index]?.model_option_ids)
			: [];

		return modelOptionData?.map((res: ModelOptionDataType) => {
			let _model = models.find(
				(model: Model) => model.ID === res.model_id?.ID
			);

			return {
				model: {
					model_name: _model?.model_name,
					part_number: _model?.part_number,
					dealer_price: res.model_id?.[type],
					MSRP: res.model_id?.MSRP,
				},
				options: getOptionData1(options, res.option_ids),
				qty: res.qty,
			};
		});
	};

	const quoteExpirationDate = (quote: any) => {
		if (!company?.createdAt) return "";

		let date = new Date(quote?.createdAt);

		const expirationNumber =
			Number(company?.quote_expiration_setting_number) || 0;

		if (company?.quote_expiration_setting_date === "month") {
			date.setMonth(date.getMonth() + expirationNumber);
		} else if (company?.quote_expiration_setting_date === "year") {
			date.setFullYear(date.getFullYear() + expirationNumber);
		} else {
			date.setDate(date.getDate() + expirationNumber);
		}
		return moment(date).format("MM/DD/YYYY");
	};

	const handleDownloadCSV = () => {
		const headers = headerData.join(",") + ",Expiration Date\n";

		const escapeCSV = (value: string | number | null | undefined): string => {
			if (value === null || value === undefined) return "";
			const str = String(value).replace(/"/g, '""');
			return `"${str}"`;
		};

		const csvRows = data?.map((quote: any, index: number) => {
			const dealer =
				quote.dealer_id === "ADMIN"
					? company?.company_name
					: quote.dealer_name;

			const contact =
				quote.dealer_id === "ADMIN"
					? company?.contact_name
					: quote?.contact_name;

			const optRef = quote.opt_reference;
			const quoteNo = quote.no;
			const createdAt = moment(quote.createdAt).format("MM/DD/YYYY");
			const status = quote.quote_status;
			const dealerPrice = subTotal(
				priceValue(index, "dealer_price"),
				company,
				"dealer_price"
			)?.toFixed(2);
			const msrp = subTotal(
				priceValue(index, "MSRP"),
				company,
				"MSRP"
			)?.toFixed(2);
			const expiration = quoteExpirationDate(quote);

			const row = [
				dealer,
				contact,
				optRef,
				quoteNo,
				createdAt,
				status,
				dealerPrice,
				msrp,
				expiration,
			];

			return row.map(escapeCSV).join(",");
		});

		const csvString = headers + csvRows.join("\n");

		const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" });
		const link = document.createElement("a");
		link.href = URL.createObjectURL(blob);
		link.setAttribute("download", "table.csv");
		document.body.appendChild(link);
		link.click();
		URL.revokeObjectURL(link.href);
		document.body.removeChild(link);
	};

	// Count quotes in the last 30 days from allData
	const last30Count = data?.filter((q: any) =>
		moment(q.createdAt).isAfter(moment().subtract(30, "days"))
	)?.length || 0;

	return (
		<Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>
			{/* Welcome Section */}
			<Stack
				direction={{ xs: "column", sm: "row" }}
				justifyContent="space-between"
				alignItems={{ xs: "flex-start", sm: "center" }}
				sx={{ mb: 4 }}
				spacing={2}
			>
				<Box>
					<Typography
						variant="h1"
						sx={{ fontSize: { xs: "1.5rem", md: "2rem" }, fontWeight: 700 }}
					>
						Welcome, {dealer?.dealer_name || currentUser?.first_name || "Dealer"}
					</Typography>
					<Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
						Manage your quotes and track your activity.
					</Typography>
				</Box>
				<Button
					variant="contained"
					color="secondary"
					startIcon={<AddIcon />}
					onClick={() => history("/_models")}
					sx={{ textTransform: "none", fontWeight: 600 }}
				>
					New Quote
				</Button>
			</Stack>

			{/* KPI Cards */}
			<Box
				sx={{
					display: "flex",
					flexWrap: "wrap",
					gap: 2.5,
					mb: 4,
				}}
			>
				<KPICard
					title="My Quotes (Last 30 Days)"
					value={isLoading ? "" : last30Count}
					icon={<RequestQuoteIcon sx={{ fontSize: 28 }} />}
					color="#3B82F6"
					loading={isLoading}
				/>
				<KPICard
					title="Year to Date"
					value={isLoading ? "" : ytdCount}
					icon={<CalendarMonthIcon sx={{ fontSize: 28 }} />}
					color="#F59E0B"
					loading={isLoading}
				/>
				<KPICard
					title="Total Sold"
					value={isLoading ? "" : totalSold}
					icon={<CheckCircleIcon sx={{ fontSize: 28 }} />}
					color="#10B981"
					loading={isLoading}
				/>
				<KPICard
					title="Total Quotes"
					value={isLoading ? "" : count}
					icon={<InventoryIcon sx={{ fontSize: 28 }} />}
					color="#00B4D8"
					loading={isLoading}
				/>
			</Box>

			{/* Recent Quotes Section */}
			<Card sx={{ overflow: "visible" }}>
				<CardContent sx={{ p: { xs: 2, md: 3 }, "&:last-child": { pb: 3 } }}>
					<Stack
						direction={{ xs: "column", sm: "row" }}
						justifyContent="space-between"
						alignItems={{ xs: "flex-start", sm: "center" }}
						sx={{ mb: 2.5 }}
						spacing={1.5}
					>
						<Typography variant="h3" sx={{ fontWeight: 600 }}>
							Recent Quotes
						</Typography>
						<Button
							variant="outlined"
							size="small"
							startIcon={<DownloadIcon />}
							onClick={handleDownloadCSV}
							disabled={isLoading || !quotes?.length}
							sx={{ textTransform: "none" }}
						>
							Download CSV
						</Button>
					</Stack>

					{isLoading ? (
						<Stack spacing={1}>
							{[...Array(5)].map((_, i) => (
								<Skeleton key={i} variant="rectangular" height={48} sx={{ borderRadius: 1 }} />
							))}
						</Stack>
					) : (
						<DefaultTable
							headerData={headerData}
							rowsData={quotes}
							rowsPerPage={pageSize}
							page={page}
							count={count}
							handleChangePage={(
								e: React.ChangeEvent<HTMLInputElement>,
								page: number
							) => handleChangePage(page)}
							handleChangeRowsPerPage={(
								e: React.ChangeEvent<HTMLInputElement>
							) => handleChangeRowsPerPage(e)}
						/>
					)}
				</CardContent>
			</Card>
		</Box>
	);
};
